/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { AXIS_DEFAULTS } from '#PRIVATE_Axis/utils/axisDefaults';
import {
  getAxisMaxSize,
  getAxisRange,
  getGroupWidthRatios,
  getSafeSkips,
  getTickLabelAnchor
} from '#PRIVATE_Axis/utils/axisUtils';
import {
  getGroupAxisLabelsInfo,
  getMaxHeight,
  getMaxWidth,
  getTickLabelInfo,
  updateLabelsDims
} from '#PRIVATE_Axis/utils/labelUtils';
import { getTitleProps } from '#PRIVATE_Axis/utils/titleUtils';
import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { getBarGapRatio } from '#UNSAFE_BarChart/utils/barSeriesUtils';
import { ScaleLinear } from '#utils/PRIVATE_chartUtils';
import { VariableWidthScale } from '#utils/PRIVATE_chartUtils/scale';
import { AxisPosition, Group, GroupAxisProps } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

import {
  getLabelCoord,
  getLevelEndingGroup,
  getLevelStartingGroup,
  getTicks,
  GroupLevelItem,
  LevelsInfo
} from './hierarchicalUtils';
import { getSeparatorPaths, getSeparatorStartLevel } from './separator';
import { applyInitialZooming } from '#utils/PRIVATE_chartUtils/overviewUtils';

/**
 * Retuns the info about group axis.
 * @param groups The groups array.
 * @param xAxis The xAxis props.
 * @param offset The start and end offset for the chart.
 * @returns
 */
export function getGroupAxisInfo(
  leafGroups: Group[],
  xAxis: GroupAxisProps = {},
  startOffset: number,
  endOffset: number,
  initialZooming?: 'first' | 'last' | 'none',
  getTextDimensions?: GetTextDimensionsType,
  availSpace?: Dimension,
  isHoriz?: boolean,
  zoomAndScroll?: 'live' | 'off'
) {
  const xAxisFontStyle = { ...xAxis?.tickLabel?.style };
  let { viewportMin, viewportMax, viewportEndGroup, viewportStartGroup } = xAxis;
  const groupCount = leafGroups.length;
  const numGroups = groupCount - 1;
  const globalMin = 0;
  const globalMax = numGroups;
  const { min, max } = applyInitialZooming({
    zoomAndScroll,
    initialZooming,
    currentMin: viewportMin,
    currentMax: viewportMax,
    globalMin,
    globalMax,
    startOffset,
    endOffset,
    numGroups,
    availSpace,
    isHoriz,
    getTextDimensions,
    tickLabelStyle: xAxisFontStyle
  });
  viewportMin = min ?? viewportMin;
  viewportMax = max ?? viewportMax;

  if (viewportMin != null) {
    const groupIndex = Math.max(0, Math.ceil(viewportMin));
    viewportStartGroup = leafGroups[isNaN(groupIndex) ? 0 : groupIndex].id;
  }

  if (viewportStartGroup != null && viewportMin == null) {
    viewportMin = leafGroups.findIndex((value: Group) => value.id === viewportStartGroup);
  }

  if (viewportMin == null) {
    viewportMin = startOffset;
    viewportStartGroup = leafGroups[0]?.id;
  }

  if (viewportMax != null) {
    const groupIndex = Math.min(groupCount, Math.floor(viewportMax));
    viewportEndGroup = leafGroups[groupIndex < 0 ? 0 : groupIndex]?.id;
  }

  if (viewportEndGroup != null && viewportMax == null) {
    viewportMax = leafGroups.findIndex((value: Group) => value.id === viewportEndGroup);
  }

  if (viewportMax == null) {
    viewportMax = groupCount - 1 + endOffset;
    viewportEndGroup = leafGroups[Math.floor(viewportMax)]?.id;
  }

  const ticks = getTicks(0, leafGroups.length - 1);

  return {
    min: startOffset,
    max: leafGroups.length - 1 + endOffset,
    viewportMin,
    viewportMax,
    viewportStartGroup,
    viewportEndGroup,
    startIndex: Math.max(0, Math.ceil(viewportMin!)),
    endIndex: Math.min(Math.floor(viewportMax), viewportMax!),
    ticks,
    minorTicks: [],
    step: 1,
    minAxisExtent: 1
  };
}

/**
 * The function that returns the x axis properties.
 * @param groupAxisIno The GroupAxisInfo.
 * @param groupAxis The groupAxis props.
 * @param groups The chart groups.
 * @param position The position of axis in the given space.
 * @param availSpace The available space.
 * @param isRtl Whether the reading direction is right to left.
 * @param getTextDimensions Function for text measurement.
 * @returns
 */
export function getGroupAxisProps(
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  viewportMin: number,
  viewportMax: number,
  min: number,
  max: number,
  startIndex: number,
  endIndex: number,
  isDomainFlipped: boolean,
  levelsArray: GroupLevelItem[][],
  endOffset: number,
  barWidths: number[],
  isStacked: boolean,
  groupAxis?: GroupAxisProps,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensionsType,
  isTestEnv?: boolean,
  groupCount?: number,
  hasWidthWeightValue?: boolean
) {
  const [start, end] = getAxisRange(availSpace, position, isRtl);
  const domain: [number, number] = isDomainFlipped
    ? [viewportMax, viewportMin]
    : [viewportMin, viewportMax];
  const gapRatio = getBarGapRatio(isStacked, startIndex, endIndex);
  const { groupWidths, averageGroupZ } = getGroupWidthRatios(barWidths, gapRatio, groupCount!);
  const scale = hasWidthWeightValue
    ? new VariableWidthScale(domain, [start, end], groupWidths, groupCount!)
    : new ScaleLinear(domain, [start, end]);

  const axisStepWidth = Math.abs(end - start) / (viewportMax - viewportMin);

  if (groupAxis?.isRendered === false) {
    return {
      title: undefined,
      titleProps: undefined,
      titleStyle: {},
      tickLabels: [],
      tickLabelStyle: {},
      axisDim: 0,
      scale,
      defaultLabelFormatter: undefined,
      separators: [],
      isGroupLabelRendered: () => false,
      axisStepWidth,
      averageGroupZ
    };
  }

  const groupSeparators = groupAxis?.groupSeparators;
  const isHoriz = position === 'top' || position === 'bottom';
  const isSize = groupAxis?.size !== undefined;
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, groupAxis?.size, groupAxis?.maxSize);
  const titleStyle = { fontSize: axisFontSize, ...groupAxis?.titleStyle };

  const { titleDim, ...titleProps } = getTitleProps(
    groupAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    isRtl,
    getTextDimensions
  );

  const { width: titleWidth, height: titleHeight } = titleProps.dims!;
  const isTitleRendered = titleWidth > 0 && titleHeight > 0;
  const axisDim = isTitleRendered ? titleDim + AXIS_DEFAULTS.titleGap : 0;
  const isRotated = position === 'left' || position === 'right';

  if (isTitleRendered) {
    if (isRotated) {
      availSpace.width -= axisDim;
      availSpace.x += position === 'left' ? axisDim : 0;
    } else {
      availSpace.height -= axisDim;
      availSpace.y += position === 'top' ? axisDim : 0;
    }
  }

  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: groupAxis?.tickLabel?.style?.color,
    ...groupAxis?.tickLabel?.style,
    textAnchor: getTickLabelAnchor(position, isRtl, true)
  };

  const isLabelRendered = groupAxis?.tickLabel?.isRendered != false;

  const groupLabels: (TextProps & {
    index: number;
  })[] = [];
  let labelDims = 0;

  let lastRenderedLevel: number | undefined;

  const levelsInfo: LevelsInfo = new Map();

  for (let level = 0; level < levelsArray.length; level++) {
    const sIndex = getLevelStartingGroup(
      levelsArray,
      level,
      viewportMin,
      min,
      max,
      scale,
      isRtl,
      isHoriz
    );
    const eIndex = getLevelEndingGroup(
      levelsArray,
      level,
      viewportMax,
      min,
      max,
      scale,
      isRtl,
      isHoriz
    );

    const labelsInView = levelsArray[level]
      .slice(sIndex, eIndex + 1)
      .map(({ item }) => item.name || item.id);

    const numOfSafeSkips = getSafeSkips(axisStepWidth, tickLabelStyle, getTextDimensions);

    const maxLabelDim = axisMaxDim - axisDim - AXIS_DEFAULTS.labelGap;

    const isWrapAllowed = tickLabelStyle.whiteSpace != 'nowrap';

    const getLabelInfo = (
      label: string | undefined,
      index: number,
      isRotated: boolean,
      allowTruncate: boolean,
      isWrapAllowed: boolean
    ) => {
      const rangeValue = getLabelCoord(
        level,
        index + sIndex,
        startIndex,
        endIndex,
        levelsArray,
        scale
      );
      return getTickLabelInfo(
        rangeValue,
        label,
        index + sIndex,
        tickLabelStyle,
        isHoriz,
        isRotated,
        isWrapAllowed,
        allowTruncate,
        isHoriz ? axisStepWidth : maxLabelDim,
        isHoriz ? maxLabelDim : axisStepWidth,
        availSpace,
        getTextDimensions
      );
    };

    const autoRotate = groupAxis?.tickLabel?.autoRotate != false;
    const levelInfo = getGroupAxisLabelsInfo(
      labelsInView,
      getLabelInfo,
      isHoriz,
      autoRotate,
      isWrapAllowed,
      numOfSafeSkips
    );

    levelsInfo.set(level, levelInfo);

    const { maxWidth, maxHeight, tickLabels } = levelInfo;

    const isLevelRendered = isHoriz
      ? maxHeight + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim
      : maxWidth + AXIS_DEFAULTS.labelGap < axisMaxDim - axisDim;

    if (isLevelRendered) lastRenderedLevel = level;

    const getAxisPos = (index: number) => {
      return getLabelCoord(level, index, min, max, levelsArray, scale)!;
    };

    if (isLabelRendered && isLevelRendered) {
      const maxW = getMaxWidth(isSize, position, axisMaxDim, maxWidth, titleDim);
      const maxH = getMaxHeight(isSize, position, axisMaxDim, maxHeight, titleDim);
      updateLabelsDims(maxW, maxH, tickLabels, getAxisPos, availSpace, position);
      const levelDims = isHoriz ? maxH + AXIS_DEFAULTS.labelGap : maxW + AXIS_DEFAULTS.labelGap;
      labelDims += levelDims;
      tickLabels.forEach((label) => {
        label['dataProps'] = {
          'data-oj-private-object': 'group',
          'data-oj-private-level-index': level.toString(),
          'data-oj-private-group-index': label.index.toString(),
          'data-oj-private-text': label.isTruncated ? label.text : undefined,
          'data-oj-private-group-id': isTestEnv
            ? levelsArray[level][label.index].item.id
            : undefined
        };
      });
      if (position === 'left') {
        availSpace.x += levelDims;
        availSpace.width -= levelDims;
      } else if (position === 'right') {
        availSpace.width -= levelDims;
      }
      availSpace.height -= levelDims;
    }
    groupLabels.push(...tickLabels);
  }

  const axisDimensions = isTitleRendered ? labelDims + axisDim : labelDims;
  if (position === 'right' && lastRenderedLevel != undefined) {
    let width = 0;
    for (let i = 0; i < lastRenderedLevel + 1; i += 1) {
      const { maxWidth, tickLabels } = levelsInfo.get(i)!;
      const getAxisPos = (index: number) => {
        return getLabelCoord(i, index, startIndex, endIndex, levelsArray, scale)!;
      };
      tickLabels.forEach((l) => {
        l.lines.forEach((_: any, index: number) => {
          const dim = l.lineProps[index];
          const x = availSpace.x + availSpace.width + labelDims - width;
          const y = getAxisPos(l.index);
          dim.x = x;
          dim.y = y;
        });
        const textDim = l.dims!;
        const x = availSpace.x + availSpace.width + labelDims - width;
        const y = getAxisPos(l.index);
        textDim.x = x;
        textDim.y = y;
      });
      width += maxWidth + AXIS_DEFAULTS.labelGap;
    }
  }

  let separators: [number, number, number, number][] = [];
  if (groupSeparators?.isRendered != false) {
    const separatorStartLevel = getSeparatorStartLevel(lastRenderedLevel, levelsInfo);
    separators = getSeparatorPaths(
      levelsArray,
      separatorStartLevel,
      levelsInfo,
      isHoriz,
      scale,
      endOffset,
      isRtl
    );
  }

  const isGroupLabelRendered = (levelIndex: number, groupIndex: number) => {
    const dims = levelsInfo.get(levelIndex)?.tickLabels[groupIndex]?.dims;
    const text = levelsInfo.get(levelIndex)?.tickLabels[groupIndex]?.text;
    return !!(dims?.width != 0 && dims?.height != 0 && text);
  };

  return {
    title: isTitleRendered ? groupAxis?.title : undefined,
    titleProps: isTitleRendered ? titleProps : undefined,
    titleStyle,
    tickLabels: isLabelRendered ? groupLabels.filter(Boolean) : [],
    tickLabelStyle,
    axisDim: axisDimensions,
    scale,
    defaultLabelFormatter: undefined,
    separators,
    isGroupLabelRendered,
    axisStepWidth,
    averageGroupZ
  };
}
